import React, { useEffect, useMemo, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import type { CodingPreviewModel } from "./questionPreviewAdapter";
import {
  getCodeMirrorBasicSetup,
  getCodeMirrorExtensions,
  getCodeMirrorStyle,
} from "../../../../utils/codeMirrorConfig";
import {
  ExpectedOutput,
  ExpectedOutputContent,
  TabNavigation,
} from "./frontendEditorUi";
import {
  generateHTMLPreview,
  getExpectedDescription,
  validateCode,
  validateStructure,
} from "../../../../utils/htmlCssValidationUtils";
import { validateBasicHTMLStructure } from "../../../../utils/htmlStructureValidation";

type Props = { model: CodingPreviewModel };

const getFileType = (fileName: string): string =>
  fileName.split(".").pop()?.toLowerCase() || "text";

const getMessageClass = (message: string): string => {
  if (message === "Validating...") return "";
  if (
    ["Congratulations!", "Success", "Passed", "Correct"].some((m) =>
      message.includes(m)
    )
  ) {
    return "text-success";
  }
  return "text-danger";
};

const calculateSuccessRate = (results: unknown[]): number => {
  const passed = results.filter((result) => {
    if (typeof result === "boolean") return result;
    if (typeof result === "object" && result !== null && "passed" in result) {
      return Boolean((result as { passed: boolean }).passed);
    }
    return false;
  }).length;
  return results.length > 0 ? (passed / results.length) * 100 : 0;
};

const setValidationMessages = (
  successRate: number,
  setSuccessMessage: (msg: string) => void,
  setAdditionalMessage: (msg: string) => void
) => {
  if (successRate === 100) {
    setSuccessMessage("Congratulations!");
    setAdditionalMessage(
      "You have passed all the test cases. Click the submit code button."
    );
  } else {
    setSuccessMessage("Wrong Answer");
    setAdditionalMessage("You have not passed all the test cases.");
  }
};

/** Pixel-faithful FrontendEditorComponent NORMAL EDITOR VIEW (testing: RUN only). */
const FrontendCodingPreview: React.FC<Props> = ({ model }) => {
  const tabs = useMemo(() => {
    if (model.tabs && model.tabs.length > 0) {
      return model.tabs.map((t) => ({
        name: t.name,
        type: t.type || getFileType(t.name),
        content: t.content || "",
      }));
    }
    return [
      {
        name: "index.html",
        type: "html",
        content: model.template || "",
      },
    ];
  }, [model]);

  const [fileContents, setFileContents] = useState<Record<string, string>>(
    () => {
      const init: Record<string, string> = {};
      tabs.forEach((t) => {
        init[t.name] = t.content;
      });
      return init;
    }
  );
  const [activeTab, setActiveTab] = useState(tabs[0]?.name || "index.html");
  const [activeSection, setActiveSection] = useState<
    "output" | "console" | "testcases"
  >("output");
  const [activeOutputTab, setActiveOutputTab] = useState<"image" | "video">(
    "image"
  );
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [additionalMessage, setAdditionalMessage] = useState("");
  const [structureErrorMessage, setStructureErrorMessage] = useState("");
  const [hasRunCode, setHasRunCode] = useState(false);
  const [testResults, setTestResults] = useState<Record<string, unknown[]>>(
    {}
  );
  const [structureResults, setStructureResults] = useState<
    Record<string, unknown[]>
  >({});
  const [selectedTestCaseIndex, setSelectedTestCaseIndex] = useState<
    number | null
  >(null);

  useEffect(() => {
    const init: Record<string, string> = {};
    tabs.forEach((t) => {
      init[t.name] = t.content;
    });
    setFileContents(init);
    setActiveTab(tabs[0]?.name || "index.html");
    setActiveSection("output");
    setActiveOutputTab("image");
    setProcessing(false);
    setSuccessMessage("");
    setAdditionalMessage("");
    setStructureErrorMessage("");
    setHasRunCode(false);
    setTestResults({});
    setStructureResults({});
    setSelectedTestCaseIndex(null);
  }, [model.question_id, tabs]);

  const questionData = useMemo(
    () => ({
      Qn_name: model.question_id,
      question_id: model.question_id,
      Qn: model.prompt,
      requirements: model.requirements || "",
      Tabs: tabs.map((t) => ({ name: t.name, type: t.type })),
      Code_Validation: model.Code_Validation || {},
      image_path: model.ExpectedImage || "",
      video_path: model.ExpectedVideo || "",
      image_urls: model.image_urls,
      topic_name: model.topic_name,
      subtopic_name: model.sub_topic_name,
    }),
    [model, tabs]
  );

  const srcCode = useMemo(
    () => generateHTMLPreview(fileContents, model.image_urls),
    [fileContents, model.image_urls]
  );

  const onChangeFileContent = (value: string) => {
    setFileContents((prev) => ({
      ...prev,
      [activeTab]: value,
    }));
  };

  const handleTabClick = (fileName: string) => {
    setActiveTab(fileName);
    setSuccessMessage("");
    setAdditionalMessage("");
    setStructureErrorMessage("");
  };

  const handleCheckCode = async () => {
    setProcessing(true);
    try {
      setActiveSection("output");
      const currentCode = fileContents[activeTab] || "";

      if (activeTab.endsWith(".html")) {
        const basicStructureCheck = validateBasicHTMLStructure(currentCode);
        if (!basicStructureCheck.isValid) {
          let errorMessage = "";
          if (basicStructureCheck.missingElements.length > 0) {
            errorMessage += `Missing elements: ${basicStructureCheck.missingElements.join(", ")}. `;
          }
          if (basicStructureCheck.structureErrors.length > 0) {
            errorMessage += `Structure errors: ${basicStructureCheck.structureErrors.join(", ")}.`;
          }
          setSuccessMessage("Wrong Answer");
          setAdditionalMessage("You have not passed all the test cases.");
          setStructureErrorMessage(errorMessage);
          setHasRunCode(true);
          setTestResults((prev) => ({ ...prev, [activeTab]: [] }));
          setStructureResults((prev) => ({ ...prev, [activeTab]: [] }));
          setSelectedTestCaseIndex(null);
          return;
        }
      }

      const results = await validateCode(
        currentCode,
        activeTab,
        questionData,
        validateBasicHTMLStructure
      );
      const structureValidationResults = await validateStructure(
        currentCode,
        activeTab,
        questionData
      );

      setTestResults((prev) => ({ ...prev, [activeTab]: results }));
      setStructureResults((prev) => ({
        ...prev,
        [activeTab]: structureValidationResults,
      }));
      setHasRunCode(true);
      setStructureErrorMessage("");

      if (results.length === 0) return;

      setSelectedTestCaseIndex(0);
      setValidationMessages(
        calculateSuccessRate(results),
        setSuccessMessage,
        setAdditionalMessage
      );
    } finally {
      setProcessing(false);
    }
  };

  const renderEditor = () => {
    const fileType = getFileType(activeTab);
    const currentContent = fileContents[activeTab] || "";
    const extensions = getCodeMirrorExtensions(fileType, "Write your code here");

    return (
      <CodeMirror
        key={activeTab}
        className="text-xl text-start custom-codemirror"
        value={currentContent}
        height="100%"
        extensions={extensions}
        onChange={onChangeFileContent}
        style={getCodeMirrorStyle()}
        basicSetup={getCodeMirrorBasicSetup()}
      />
    );
  };

  const activeResults = (testResults[activeTab] || []) as unknown[];
  const codeValidation = (model.Code_Validation || {}) as Record<
    string,
    { structure?: unknown[] }
  >;
  const structure = codeValidation[activeTab]?.structure || [];

  return (
    <div
      className="d-flex"
      style={{ height: "70vh", width: "100%", gap: "0" }}
    >
      <div
        className="bg-white"
        style={{
          width: "40%",
          flexShrink: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRight: "2px solid #dee2e6",
          marginRight: "10px",
        }}
      >
        <div
          className="bg-white"
          style={{
            height: "100%",
            backgroundColor: "#E5E5E533",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              height: "50%",
              display: "flex",
              flexDirection: "column",
              borderBottom: "2px solid #dee2e6",
            }}
          >
            <div
              className="flex-fill overflow-auto p-3"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "#c1c1c1 #f1f1f1",
              }}
            >
              <div style={{ marginBottom: "20px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                  }}
                >
                  <h4
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#333",
                      margin: 0,
                    }}
                  >
                    Problem Statement
                  </h4>
                  {model.question_id && (
                    <span className="p-2 fs-6 rounded-2 bg-primary-subtle">
                      Topic: {model.topic_name || "-"} <br />
                      Sub topic: {model.sub_topic_name || "-"} <br />
                      QID: {model.question_id}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "inherit",
                    lineHeight: "1.5",
                    fontSize: "14px",
                    color: "#555",
                  }}
                  dangerouslySetInnerHTML={{ __html: model.prompt || "" }}
                />
              </div>

              <div>
                <h4
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    marginBottom: "10px",
                    color: "#333",
                  }}
                >
                  Requirements
                </h4>
                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      model.requirements || "No requirements specified.",
                  }}
                />
              </div>
            </div>
          </div>

          <div
            style={{ height: "50%", display: "flex", flexDirection: "column" }}
          >
            <ExpectedOutput
              questionData={questionData}
              activeOutputTab={activeOutputTab}
              onOutputTabChange={(tab) => setActiveOutputTab(tab)}
            />
            <ExpectedOutputContent
              questionData={questionData}
              activeOutputTab={activeOutputTab}
            />
          </div>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          minWidth: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          className="bg-white"
          style={{ height: "45%", backgroundColor: "#E5E5E533" }}
        >
          <div className="border-bottom border-dark p-2 d-flex justify-content-between align-items-center">
            <TabNavigation
              tabs={tabs}
              activeTab={activeTab}
              onTabClick={handleTabClick}
              showExpandButton={false}
            />
          </div>
          <div
            className="col top"
            style={{
              height: "calc(100% - 60px)",
              overflowY: "auto",
              marginBottom: "10px",
            }}
          >
            {renderEditor()}
          </div>
        </div>

        <div
          style={{ height: "6%", backgroundColor: "#E5E5E533" }}
          className="d-flex flex-column justify-content-center pe-3"
        >
          <div className="d-flex justify-content-between align-items-center h-100">
            <div className="d-flex flex-column justify-content-center">
              {processing ? (
                <h5 className="m-0 processingDivHeadingTag">Processing...</h5>
              ) : (
                <>
                  {successMessage && (
                    <h5
                      className={`m-0 ps-1 ${getMessageClass(successMessage)}`}
                      style={{ fontSize: "14px" }}
                    >
                      {successMessage}
                    </h5>
                  )}
                  {additionalMessage && (
                    <p
                      className={`processingDivParaTag m-0 ps-1 ${getMessageClass(successMessage)}`}
                      style={{ fontSize: "10px" }}
                    >
                      {additionalMessage}
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-sm btn-light me-2 processingDivButton"
                style={{
                  whiteSpace: "nowrap",
                  fontSize: "12px",
                  minWidth: "70px",
                  boxShadow: "#888 1px 2px 5px 0px",
                  height: "30px",
                }}
                onClick={handleCheckCode}
                disabled={processing}
              >
                RUN
              </button>
            </div>
          </div>
        </div>

        <div
          className="bg-white"
          style={{
            height: "48%",
            backgroundColor: "#E5E5E533",
            position: "relative",
          }}
        >
          <div
            className="p-3"
            style={{
              height: "calc(100% - 10px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="d-flex mb-3" style={{ flexShrink: 0 }}>
              <button
                type="button"
                className={`btn ${
                  activeSection === "output"
                    ? "btn-primary"
                    : "btn-outline-primary"
                } me-2`}
                onClick={() => setActiveSection("output")}
                style={{ fontSize: "12px", padding: "6px 12px" }}
              >
                Output
              </button>
              <button
                type="button"
                className={`btn ${
                  activeSection === "console"
                    ? "btn-primary"
                    : "btn-outline-primary"
                } me-2`}
                onClick={() => setActiveSection("console")}
                style={{ fontSize: "12px", padding: "6px 12px" }}
              >
                Console
              </button>
              <button
                type="button"
                className={`btn ${
                  activeSection === "testcases"
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                onClick={() => setActiveSection("testcases")}
                style={{ fontSize: "12px", padding: "6px 12px" }}
              >
                Test Cases
              </button>
            </div>

            {activeSection === "output" && (
              <div
                style={{
                  flex: 1,
                  maxHeight: "90%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {hasRunCode &&
                  activeTab.endsWith(".html") &&
                  activeResults.length === 0 &&
                  structureErrorMessage && (
                    <div
                      className="alert alert-warning m-0 me-3 align-self-center"
                      style={{
                        fontSize: "12px",
                        padding: "8px 12px",
                        margin: "0 0 10px 0",
                      }}
                    >
                      <strong>HTML Structure Error:</strong>
                      {structureErrorMessage}
                    </div>
                  )}
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {srcCode ? (
                    <iframe
                      style={{
                        width: "100%",
                        height: "100%",
                        backgroundColor: "white",
                        color: "black",
                        border: "none",
                        outline: "none",
                        overflow: "auto",
                      }}
                      className="w-full h-full"
                      srcDoc={srcCode}
                      title="Output"
                      sandbox="allow-scripts allow-same-origin"
                      width="100%"
                      height="100%"
                      scrolling="yes"
                    />
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center h-100"
                      style={{ backgroundColor: "#f8f9fa" }}
                    >
                      <div className="text-center text-muted">
                        <p className="mt-2">Click RUN to see output</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeSection === "console" && (
              <div style={{ flex: 1, maxHeight: "90%", overflow: "auto" }}>
                <div
                  className="text-center text-muted"
                  style={{ padding: "20px" }}
                >
                  <p className="mt-2">
                    {hasRunCode
                      ? "No console output captured in preview"
                      : "Click RUN to see console"}
                  </p>
                </div>
              </div>
            )}

            {activeSection === "testcases" && (
              <div style={{ flex: 1, maxHeight: "90%", overflow: "hidden" }}>
                {activeResults.length > 0 ? (
                  <div className="d-flex" style={{ height: "100%" }}>
                    <div
                      className="border-end"
                      style={{
                        width: "30%",
                        height: "100%",
                        overflowY: "auto",
                        padding: "10px",
                        scrollbarWidth: "thin",
                      }}
                    >
                      {activeResults.map((result, index) => {
                        let isPassed = false;
                        if (typeof result === "boolean") isPassed = result;
                        else if (
                          typeof result === "object" &&
                          result !== null &&
                          "passed" in result
                        ) {
                          isPassed = Boolean(
                            (result as { passed: boolean }).passed
                          );
                        }
                        return (
                          <div
                            key={index}
                            className={`p-2 border-bottom ${
                              selectedTestCaseIndex === index
                                ? "text-primary"
                                : ""
                            }`}
                            style={{
                              fontSize: "12px",
                              cursor: "pointer",
                              minHeight: "40px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              margin: "6px 0px",
                              borderRadius: "8px",
                              padding: "8px 12px",
                              backgroundColor:
                                selectedTestCaseIndex === index
                                  ? "#f2f2f0"
                                  : "#f8f9fa",
                              border: "1px solid #dee2e6",
                              color:
                                selectedTestCaseIndex === index
                                  ? "#007bff"
                                  : "#212529",
                            }}
                            onClick={() => setSelectedTestCaseIndex(index)}
                          >
                            <span>Test Case {index + 1}</span>
                            {isPassed ? (
                              <span className="text-success">✓</span>
                            ) : (
                              <span className="text-danger">✗</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div
                      className="px-4 pt-1 pb-3"
                      style={{ width: "70%", overflowY: "auto" }}
                    >
                      {selectedTestCaseIndex !== null &&
                        activeResults[selectedTestCaseIndex] !== undefined && (
                          <div>
                            <div className="mb-3">
                              <strong>Status: </strong>
                              <span
                                className={
                                  (() => {
                                    const result =
                                      activeResults[selectedTestCaseIndex];
                                    if (typeof result === "boolean") {
                                      return result
                                        ? "text-success"
                                        : "text-danger";
                                    }
                                    if (
                                      typeof result === "object" &&
                                      result &&
                                      "passed" in result
                                    ) {
                                      return (result as { passed: boolean })
                                        .passed
                                        ? "text-success"
                                        : "text-danger";
                                    }
                                    return "text-danger";
                                  })()
                                }
                              >
                                {(() => {
                                  const result =
                                    activeResults[selectedTestCaseIndex];
                                  if (typeof result === "boolean") {
                                    return result ? "Pass" : "Failed";
                                  }
                                  if (
                                    typeof result === "object" &&
                                    result &&
                                    "passed" in result
                                  ) {
                                    return (result as { passed: boolean })
                                      .passed
                                      ? "Pass"
                                      : "Failed";
                                  }
                                  return "Failed";
                                })()}
                              </span>
                            </div>
                            {structure[selectedTestCaseIndex] != null && (
                              <div className="mb-3">
                                <strong>Expected:</strong>
                                <pre
                                  className="mt-1 p-2 bg-light rounded"
                                  style={{
                                    fontSize: "11px",
                                    fontFamily: "monospace",
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {getExpectedDescription(
                                    structure[selectedTestCaseIndex],
                                    activeTab
                                  )}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                ) : (
                  <div
                    className="text-center text-muted"
                    style={{ padding: "20px" }}
                  >
                    <p className="mt-2">Click RUN to validate your code</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FrontendCodingPreview;
